import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, CreditCard, UserX, TriangleAlert as AlertTriangle } from 'lucide-react';
import type { XmCustomerPayment, XmCardBrand, CardLimitGroup } from '@/types/database';
import { CARD_LIMIT_GROUP, CARD_GROUP_LIMITS, CARD_GROUP_LABELS } from '@/types/database';
import { getCardBrandColor } from './cardBrandColors';

interface Props {
  customerId: string;
  customerName: string;
  payments: XmCustomerPayment[];
  onClose: () => void;
  onDeletePayment: (paymentId: string) => void;
  onDeleteCustomer: (customerId: string) => void;
}

export default function CustomerDetailModal({ customerId, customerName, payments, onClose, onDeletePayment, onDeleteCustomer }: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const groupSummaries = useMemo(() => {
    const map = new Map<CardLimitGroup, { charged: number; brands: Set<string> }>();
    for (const p of payments) {
      const group = CARD_LIMIT_GROUP[p.card_brand as XmCardBrand];
      if (!group) continue;
      const existing = map.get(group) || { charged: 0, brands: new Set<string>() };
      existing.charged += Number(p.charged_amount);
      existing.brands.add(p.card_brand);
      map.set(group, existing);
    }
    return Array.from(map.entries()).map(([group, data]) => ({
      group,
      label: CARD_GROUP_LABELS[group],
      fullLimit: CARD_GROUP_LIMITS[group],
      charged: data.charged,
      available: CARD_GROUP_LIMITS[group] - data.charged,
      brands: Array.from(data.brands),
    }));
  }, [payments]);

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-modal rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-base font-bold text-white">{customerName}</h2>
            <p className="text-xs text-white mt-0.5">{payments.length} payment{payments.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setConfirmingDelete(true)}
              className="text-white hover:text-red-500 p-1.5 glass-hover-red rounded-lg"
              title="Delete customer"
            >
              <UserX size={16} />
            </button>
            <button
              onClick={onClose}
              className="text-white hover:text-white p-1 glass-hover rounded-lg"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {groupSummaries.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {groupSummaries.map(g => (
                <div key={g.group} className="glass-inner rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="flex -space-x-1">
                      {g.brands.map(b => {
                        const color = getCardBrandColor(b);
                        return <div key={b} className={`w-2 h-2 rounded-full ring-1 ring-white ${color.dot}`} />;
                      })}
                    </div>
                    <span className="text-[10px] font-semibold text-white uppercase tracking-wider">{g.label}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-center">
                    <div>
                      <p className="text-[10px] text-white">Limit</p>
                      <p className="text-xs font-bold text-white">${g.fullLimit.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white">Used</p>
                      <p className="text-xs font-bold text-white">${g.charged.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white">Avail</p>
                      <p className={`text-xs font-bold ${g.available >= 0 ? 'text-emerald-800' : 'text-red-600'}`}>
                        ${g.available.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 bg-white/10 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        g.charged / g.fullLimit > 0.9 ? 'bg-red-500' : g.charged / g.fullLimit > 0.7 ? 'bg-amber-500' : 'bg-teal-500'
                      }`}
                      style={{ width: `${Math.min(100, (g.charged / g.fullLimit) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="bg-white/10 rounded-2xl p-4 mb-3">
                <CreditCard size={24} className="text-white" />
              </div>
              <p className="text-xs text-white">No payments recorded</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              <p className="text-[10px] font-semibold text-white uppercase tracking-wider">Payment History</p>
              {payments.map(p => {
                const color = getCardBrandColor(p.card_brand);
                return (
                  <div key={p.id} className="glass-inner rounded-xl p-3.5 flex items-start gap-3 group">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${color.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-white">{p.card_brand}</span>
                        <span className="text-[10px] text-white">
                          {new Date(p.payment_date).toLocaleDateString()} {new Date(p.payment_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="text-[11px]">
                        <span className="text-white">Charged: </span>
                        <span className="font-semibold text-white">${Number(p.charged_amount).toFixed(2)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => onDeletePayment(p.id)}
                      className="text-white hover:text-red-500 p-1 glass-hover-red rounded-lg opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {confirmingDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white/10 rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 text-center">
              <div className="mx-auto w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle size={22} className="text-red-500" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1.5">Delete Customer</h3>
              <p className="text-xs text-white leading-relaxed">
                Are you sure you want to delete <span className="font-semibold text-white">{customerName}</span> and all their payments? This action cannot be undone.
              </p>
            </div>
            <div className="flex border-t border-white/10">
              <button
                onClick={() => setConfirmingDelete(false)}
                className="flex-1 px-4 py-3 text-xs font-medium text-white glass-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setConfirmingDelete(false);
                  onDeleteCustomer(customerId);
                }}
                className="flex-1 px-4 py-3 text-xs font-semibold text-red-600 glass-hover-red border-l border-white/10"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
